import { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import Card from "@/components/base/Card";
import Badge from "@/components/base/Badge";
import Input from "@/components/base/Input";
import Button from "@/components/base/Button";
import { caseService, estudianteService } from "@/services/api";

interface PsychologyPlan {
  id: string;
  studentName: string;
  studentId: string;
  curso: string;
  planActivo: string;
  ultimaSesion: string;
  proximaCita: string;
  hitos: string;
  estado: string; // "activo" | "warning" | "critico"
  descripcion: string;
  judicializado: boolean;
  confidencial: boolean;
  responsableId?: string;
}

export default function PsicologoSeguimientoPage() {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("todos");
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [plans, setPlans] = useState<PsychologyPlan[]>([]);
  const [students, setStudents] = useState<any[]>([]);

  // Modal states
  const [isAdding, setIsAdding] = useState(false);
  const [isViewing, setIsViewing] = useState<PsychologyPlan | null>(null);
  const [isEditing, setIsEditing] = useState<PsychologyPlan | null>(null);

  // Form states for new/edit
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [planActivo, setPlanActivo] = useState("");
  const [ultimaSesion, setUltimaSesion] = useState(new Date().toISOString().split("T")[0]);
  const [proximaCita, setProximaCita] = useState("");
  const [hitos, setHitos] = useState("");
  const [estado, setEstado] = useState("activo");

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");
      const [casesData, studentsData] = await Promise.all([
        caseService.list({ tipo: "psicologico" }),
        estudianteService.list()
      ]);

      if (Array.isArray(studentsData)) {
        setStudents(studentsData);
      }

      if (Array.isArray(casesData)) {
        const mapped: PsychologyPlan[] = casesData.map((c: any) => {
          let planEstado = "activo";
          if (c.estado === "en_evaluacion") planEstado = "warning";
          else if (c.estado === "en_intervencion" || c.prioridad === "critica") planEstado = "critico";

          return {
            id: c.id,
            studentName: c.estudiante_nombre || "Estudiante",
            studentId: c.estudiante_id,
            curso: c.estudiante_curso || "Curso",
            planActivo: c.titulo,
            ultimaSesion: c.fecha_apertura ? new Date(c.fecha_apertura).toISOString().split("T")[0] : "",
            proximaCita: c.fecha_limite ? new Date(c.fecha_limite).toISOString().split("T")[0] : "",
            hitos: c.subtipo || "0 de 5 Hitos",
            estado: planEstado,
            descripcion: c.descripcion || "",
            judicializado: !!c.judicializado,
            confidencial: !!c.confidencial,
            responsableId: c.responsable_id
          };
        });
        setPlans(mapped);
      }
    } catch (err: any) {
      console.error("Error loading psychology plans:", err);
      setError("Error al cargar planes de seguimiento: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filtered = useMemo(() => {
    let list = [...plans];
    
    // Status Filter
    if (filter !== "todos") {
      list = list.filter(p => {
        if (filter === "critico") return p.estado === "critico";
        return p.estado === filter;
      });
    }

    // Search query
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p => 
        p.studentName.toLowerCase().includes(q) || 
        p.curso.toLowerCase().includes(q) || 
        p.planActivo.toLowerCase().includes(q)
      );
    }

    return list;
  }, [plans, filter, search]);

  const handleExport = () => {
    const csvHeaders = ["Estudiante", "Curso", "Plan Activo", "Última Sesión", "Próxima Cita", "Hitos Alcanzados", "Estado"];
    const rows = filtered.map(p => [
      p.studentName,
      p.curso,
      p.planActivo,
      p.ultimaSesion,
      p.proximaCita,
      p.hitos,
      p.estado
    ]);
    
    const csvContent = "\uFEFF" + [csvHeaders.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Seguimiento_Psicologia_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  const handleOpenAdd = () => {
    setSelectedStudentId("");
    setPlanActivo("");
    setUltimaSesion(new Date().toISOString().split("T")[0]);
    setProximaCita("");
    setHitos("0 de 5 Hitos");
    setEstado("activo");
    setIsAdding(true);
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const studentObj = students.find(s => s.id === selectedStudentId);
    if (!studentObj) {
      alert("Por favor seleccione un estudiante.");
      return;
    }

    try {
      setLoading(true);
      // Map frontend state to backend case priority/status
      let dbPrioridad = "media";
      let dbEstado = "en_seguimiento";
      if (estado === "warning") {
        dbPrioridad = "alta";
        dbEstado = "en_evaluacion";
      } else if (estado === "critico") {
        dbPrioridad = "critica";
        dbEstado = "en_intervencion";
      }

      const newCase = await caseService.create({
        estudiante_id: selectedStudentId,
        tipo: "psicologico",
        subtipo: hitos,
        titulo: planActivo,
        descripcion: `Plan de acompañamiento activo: ${planActivo}`,
        prioridad: dbPrioridad,
        fecha_limite: proximaCita || null,
        judicializado: false,
        confidencial: true
      });

      if (newCase && newCase.id) {
        // Transition status if needed
        await caseService.transitionStatus(newCase.id, dbEstado);
      }

      setIsAdding(false);
      await loadData();
    } catch (err: any) {
      console.error("Error creating plan:", err);
      alert("Error al guardar plan: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEdit = (plan: PsychologyPlan) => {
    setIsEditing(plan);
    setPlanActivo(plan.planActivo);
    setUltimaSesion(plan.ultimaSesion);
    setProximaCita(plan.proximaCita);
    setHitos(plan.hitos);
    setEstado(plan.estado);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEditing) return;

    try {
      setLoading(true);
      let dbPrioridad = "media";
      let dbEstado = "en_seguimiento";
      if (estado === "warning") {
        dbPrioridad = "alta";
        dbEstado = "en_evaluacion";
      } else if (estado === "critico") {
        dbPrioridad = "critica";
        dbEstado = "en_intervencion";
      }

      await caseService.update(isEditing.id, {
        titulo: planActivo,
        descripcion: isEditing.descripcion || `Plan de acompañamiento activo: ${planActivo}`,
        prioridad: dbPrioridad,
        fecha_limite: proximaCita || null,
        subtipo: hitos,
        judicializado: isEditing.judicializado,
        confidencial: isEditing.confidencial,
        responsable_id: isEditing.responsableId || null
      });

      await caseService.transitionStatus(isEditing.id, dbEstado);

      setIsEditing(null);
      await loadData();
    } catch (err: any) {
      console.error("Error updating plan:", err);
      alert("Error al actualizar plan: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const getStatusVariant = (st: string) => {
    const s = st.toLowerCase();
    if (s === "activo" || s === "primary") return "primary";
    if (s === "warning" || s === "alerta") return "warning";
    if (s === "danger" || s === "critico") return "danger";
    return "neutral";
  };

  return (
    <div className="p-4 md:p-6 space-y-5 animate-fade-in-up">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Seguimiento de Estudiantes</h1>
          <p className="text-sm text-foreground-500 mt-0.5">Planes de acompañamiento psicológico activo, hitos alcanzados y compromisos institucionales vigentes.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={handleExport} className="cursor-pointer">
            <i className="ri-download-line mr-1.5" /> Exportar
          </Button>
          <Button size="sm" onClick={handleOpenAdd} className="cursor-pointer bg-primary-500 text-white">
            <i className="ri-add-line mr-1.5" /> Agregar Registro
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <i className="ri-error-warning-line mr-1" /> {error}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
        <Input
          placeholder="Buscar..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          icon={<i className="ri-search-line" />}
          className="max-w-sm"
          fullWidth={false}
        />
        <div className="flex gap-2 overflow-x-auto">
          {["todos", "activo", "pendiente", "critico"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold border cursor-pointer capitalize transition-all ${
                filter === f
                  ? "bg-foreground-900 text-white border-foreground-900"
                  : "bg-white text-foreground-600 border-background-200 hover:bg-background-50"
              }`}
            >
              {f === "todos" ? "Todos" : f === "critico" ? "⚠️ Críticos" : f}
            </button>
          ))}
        </div>
      </div>

      <Card padding="none">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="py-12 text-center text-foreground-500">
              <i className="ri-loader-4-line animate-spin mr-2" /> Cargando...
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-50/50">
                  <th className="text-left py-3.5 px-4 text-foreground-500 font-semibold text-xs uppercase tracking-wider">Estudiante</th>
                  <th className="text-left py-3.5 px-4 text-foreground-500 font-semibold text-xs uppercase tracking-wider">Curso</th>
                  <th className="text-left py-3.5 px-4 text-foreground-500 font-semibold text-xs uppercase tracking-wider">Plan Activo</th>
                  <th className="text-left py-3.5 px-4 text-foreground-500 font-semibold text-xs uppercase tracking-wider">Última Sesión</th>
                  <th className="text-left py-3.5 px-4 text-foreground-500 font-semibold text-xs uppercase tracking-wider">Próxima Cita</th>
                  <th className="text-left py-3.5 px-4 text-foreground-500 font-semibold text-xs uppercase tracking-wider">Hitos Alcanzados</th>
                  <th className="text-right py-3.5 px-4 text-foreground-500 font-semibold text-xs uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                    <td className="py-3.5 px-4 font-semibold text-foreground-900 whitespace-nowrap">
                      {p.studentName}
                    </td>
                    <td className="py-3.5 px-4 text-foreground-600 whitespace-nowrap text-xs">
                      {p.curso}
                    </td>
                    <td className="py-3.5 px-4 text-foreground-700 font-medium text-xs">
                      {p.planActivo}
                    </td>
                    <td className="py-3.5 px-4 text-foreground-600 whitespace-nowrap text-xs">
                      {p.ultimaSesion}
                    </td>
                    <td className="py-3.5 px-4 text-foreground-600 whitespace-nowrap text-xs">
                      {p.proximaCita || "—"}
                    </td>
                    <td className="py-3.5 px-4 whitespace-nowrap">
                      <Badge variant={getStatusVariant(p.estado)}>{p.hitos}</Badge>
                    </td>
                    <td className="py-3.5 px-4 text-right whitespace-nowrap">
                      <button 
                        onClick={() => setIsViewing(p)}
                        className="text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer p-1"
                      >
                        <i className="ri-eye-line text-base" />
                      </button>
                      <button 
                        onClick={() => handleOpenEdit(p)}
                        className="text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer p-1 ml-1.5"
                      >
                        <i className="ri-edit-line text-base" />
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-foreground-400">
                      No se encontraron registros que coincidan con la búsqueda.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {/* ── MODAL: AGREGAR REGISTRO ── */}
      {isAdding && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setIsAdding(false)}>
          <form onSubmit={handleAddSubmit} className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4 shadow-xl animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center pb-2 border-b border-background-150">
              <h2 className="font-bold text-foreground-950 text-base">Nuevo Plan de Acompañamiento</h2>
              <button type="button" onClick={() => setIsAdding(false)} className="cursor-pointer text-foreground-400 hover:text-foreground-700">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Estudiante</label>
                <select
                  value={selectedStudentId}
                  onChange={(e) => setSelectedStudentId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                  required
                >
                  <option value="">Seleccione estudiante...</option>
                  {students.map((s) => (
                    <option key={s.id} value={s.id}>{s.nombre} - {s.curso}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nombre del Plan Activo</label>
                <input
                  type="text"
                  value={planActivo}
                  onChange={(e) => setPlanActivo(e.target.value)}
                  placeholder="Ej: Taller de Regulación Conductual"
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Última Sesión</label>
                  <input
                    type="date"
                    value={ultimaSesion}
                    onChange={(e) => setUltimaSesion(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Próxima Cita</label>
                  <input
                    type="date"
                    value={proximaCita}
                    onChange={(e) => setProximaCita(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Hitos</label>
                <input
                  type="text"
                  value={hitos}
                  onChange={(e) => setHitos(e.target.value)}
                  placeholder="Ej: 1 de 5 Hitos"
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Estado del Plan</label>
                <select
                  value={estado}
                  onChange={(e) => setEstado(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                >
                  <option value="activo">Activo (Verde)</option>
                  <option value="warning">Alerta (Amarillo)</option>
                  <option value="critico">Crítico (Rojo)</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1 bg-primary-500 text-white cursor-pointer">
                Guardar Plan
              </Button>
              <Button type="button" variant="ghost" onClick={() => setIsAdding(false)} className="flex-1 cursor-pointer">
                Cancelar
              </Button>
            </div>
          </form>
        </div>,
        document.body
      )}

      {/* ── MODAL: EDITAR REGISTRO ── */}
      {isEditing && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setIsEditing(null)}>
          <form onSubmit={handleEditSubmit} className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4 shadow-xl animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center pb-2 border-b border-background-150">
              <h2 className="font-bold text-foreground-950 text-base">Editar Plan: {isEditing.studentName}</h2>
              <button type="button" onClick={() => setIsEditing(null)} className="cursor-pointer text-foreground-400 hover:text-foreground-700">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nombre del Plan Activo</label>
                <input
                  type="text"
                  value={planActivo}
                  onChange={(e) => setPlanActivo(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Última Sesión</label>
                  <input
                    type="date"
                    value={ultimaSesion}
                    onChange={(e) => setUltimaSesion(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Próxima Cita</label>
                  <input
                    type="date"
                    value={proximaCita}
                    onChange={(e) => setProximaCita(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Hitos</label>
                <input
                  type="text"
                  value={hitos}
                  onChange={(e) => setHitos(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Estado del Plan</label>
                <select
                  value={estado}
                  onChange={(e) => setEstado(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
                >
                  <option value="activo">Activo (Verde)</option>
                  <option value="warning">Alerta (Amarillo)</option>
                  <option value="critico">Crítico (Rojo)</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1 bg-primary-500 text-white cursor-pointer">
                Guardar Cambios
              </Button>
              <Button type="button" variant="ghost" onClick={() => setIsEditing(null)} className="flex-1 cursor-pointer">
                Cancelar
              </Button>
            </div>
          </form>
        </div>,
        document.body
      )}

      {/* ── MODAL: VER REGISTRO ── */}
      {isViewing && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setIsViewing(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 space-y-4 shadow-xl animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center pb-2 border-b border-background-150">
              <h2 className="font-bold text-foreground-950 text-base">Plan: {isViewing.studentName}</h2>
              <button type="button" onClick={() => setIsViewing(null)} className="cursor-pointer text-foreground-400 hover:text-foreground-700">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="space-y-2 text-xs">
              <p className="text-foreground-500"><strong className="text-foreground-700 text-sm">Plan Activo:</strong> {isViewing.planActivo}</p>
              <p className="text-foreground-500"><strong className="text-foreground-700">Curso:</strong> {isViewing.curso}</p>
              <p className="text-foreground-500"><strong className="text-foreground-700">Última Sesión:</strong> {isViewing.ultimaSesion}</p>
              <p className="text-foreground-500"><strong className="text-foreground-700">Próxima Cita:</strong> {isViewing.proximaCita || "Sin agendar"}</p>
              <p className="text-foreground-500"><strong className="text-foreground-700">Hitos:</strong> {isViewing.hitos}</p>
              <p className="text-foreground-500"><strong className="text-foreground-700">Estado del Plan:</strong> <Badge variant={getStatusVariant(isViewing.estado)}>{isViewing.estado.toUpperCase()}</Badge></p>
            </div>
            <div className="pt-2">
              <Button type="button" onClick={() => setIsViewing(null)} className="w-full bg-primary-500 text-white cursor-pointer">
                Cerrar
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
